import React, { useState, useEffect, useRef } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useListContext } from 'react-admin';
import './CustomSearchInput.css';

type CustomSearchInputProps = {
  variant?: 'filled' | 'outlined' | 'standard';
  name: string;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  label?: string;
  fullWidth?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  alwaysOn?: boolean;
  sx?: any;
};

/**
 * CustomSearchInput - компонент поиска с автоматической блокировкой во время загрузки
 * 
 * ВАЖНО: Этот компонент должен использоваться ТОЛЬКО внутри компонента List
 * или в контексте, где доступен useListContext.
 * 
 * Решает проблему потери фокуса при обновлении данных:
 * - Поле автоматически блокируется (disabled) когда isLoading = true
 * - Кнопки поиска и очистки также блокируются
 * - Фокус сохраняется после завершения загрузки
 * - Визуальное состояние фокуса поддерживается даже в заблокированном состоянии
 * 
 * ИСПРАВЛЕНО: Добавлена логика сохранения фокуса и состояния focused
 * для предотвращения потери фокуса при обновлении данных
 */
const CustomSearchInput: React.FC<CustomSearchInputProps> = ({
  variant = 'filled',
  name,
  autoFocus = false,
  onFocus,
  onBlur,
  placeholder,
  label,
  fullWidth = true,
  margin = 'none',
  sx = {},
}) => {
  // Получаем контекст списка - этот хук должен вызываться только внутри List
  const { setFilters, filterValues, isLoading } = useListContext();
  
  const [searchValue, setSearchValue] = useState(filterValues[name] || '');
  const userInputRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastFilterValueRef = useRef(filterValues[name]);
  const wasFocusedRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);

  // Синхронизируем состояние только если фильтр был изменен извне (не пользователем)
  useEffect(() => {
    const currentFilterValue = filterValues[name];
    
    // Проверяем, изменился ли фильтр извне
    if (lastFilterValueRef.current !== currentFilterValue && !userInputRef.current) {
      setSearchValue(currentFilterValue || '');
      lastFilterValueRef.current = currentFilterValue;
    }
  }, [filterValues, name, setSearchValue]);

  // Сохраняем фокус после завершения загрузки
  useEffect(() => {
    if (!isLoading && wasFocusedRef.current && inputRef.current) {
      // Небольшая задержка для корректного восстановления фокуса
      setTimeout(() => {
        if (inputRef.current && !isLoading) {
          inputRef.current.focus();
          wasFocusedRef.current = false;
        }
      }, 50);
    }
  }, [isLoading]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      userInputRef.current = false;
      performSearch();
    }
  };

  const handleSearchClick = () => {
    performSearch();
  };

  const handleClearClick = () => {
    setSearchValue('');
    const newFilters = {
      ...filterValues,
      [name]: undefined,
    };

    // Удаляем пустые значения фильтров
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key] === '' || newFilters[key] === undefined) {
        delete newFilters[key];
      }
    });

    setFilters(newFilters, {});
  };

  const performSearch = () => {
    const newFilters = {
      ...filterValues,
      [name]: searchValue.trim() || undefined,
    };

    // Удаляем пустые значения фильтров
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key] === '' || newFilters[key] === undefined) {
        delete newFilters[key];
      }
    });

    setFilters(newFilters, {});
  };

  const handleFocus = () => {
    setIsFocused(true);
    wasFocusedRef.current = true;
    onFocus?.();
  };

  const handleBlur = () => {
    // Не сбрасываем флаг фокуса при потере фокуса, если поле заблокировано
    if (!isLoading) {
      setIsFocused(false);
      wasFocusedRef.current = false;
    }
    onBlur?.();
  };

  // Определяем, какая иконка должна отображаться
  const hasValue = searchValue.trim().length > 0;
  
  return (
    <TextField
      ref={inputRef}
      variant={variant}
      name={name}
      value={searchValue}
      onChange={(e) => {
        userInputRef.current = true;
        setSearchValue(e.target.value);
      }}
      onKeyPress={handleKeyPress}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder || 'Поиск'}
      label={label}
      fullWidth={fullWidth}
      margin={margin}
      autoFocus={autoFocus}
      disabled={isLoading}
      focused={isFocused}
      sx={{
        minWidth: 200,
        '& .MuiInputBase-root': {
          minHeight: '40px',
        },
        '& .MuiInputBase-input': {
          paddingTop: '18px',
          paddingBottom: '8px',
        },
        ...sx
      }}
      inputProps={{
        'data-focused': isFocused ? 'true' : 'false'
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={hasValue ? handleClearClick : handleSearchClick}
              edge="end"
              size="small"
              sx={{ padding: '4px' }}
              disabled={isLoading}
            >
              {hasValue ? <ClearIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default CustomSearchInput;
